import type { Message } from "@/pages/ask/components/MessageBubble";
import type { Brand } from "@/pages/ask/lib/data";

export type TranscriptBlock = {
  kind: "question" | "answer" | "finding" | "table";
  text: string;
};

const MAX_TABLE_ROWS = 10;

function answerBlocks(message: Message): TranscriptBlock[] {
  const response = message.queryResponse;
  if (!response) {
    return message.content ? [{ kind: "answer", text: message.content }] : [];
  }

  const blocks: TranscriptBlock[] = [];
  if (response.error) blocks.push({ kind: "answer", text: response.error });
  if (response.explanation) blocks.push({ kind: "answer", text: response.explanation });
  for (const finding of response.findings ?? []) blocks.push({ kind: "finding", text: finding });

  const lastStep = response.steps?.[response.steps.length - 1];
  const columns = lastStep?.columns ?? response.columns ?? [];
  const rows = lastStep?.rows ?? response.results ?? [];
  if (columns.length > 0 && rows.length > 0) {
    blocks.push({ kind: "table", text: columns.join(" | ") });
    for (const row of rows.slice(0, MAX_TABLE_ROWS)) {
      blocks.push({ kind: "table", text: columns.map((column) => String(row[column] ?? "")).join(" | ") });
    }
    if (rows.length > MAX_TABLE_ROWS) {
      blocks.push({ kind: "table", text: `… ${rows.length - MAX_TABLE_ROWS} more rows` });
    }
  }

  return blocks;
}

export function transcriptBlocks(messages: Message[]): TranscriptBlock[] {
  return messages.flatMap((message) =>
    message.role === "user"
      ? message.content
        ? [{ kind: "question" as const, text: message.content }]
        : []
      : answerBlocks(message),
  );
}

export function transcriptFileName(scope: Brand, date: Date): string {
  const brand = (scope.displayName || scope.name || "chat").toLowerCase().replace(/[^a-z0-9]+/g, "-");
  return `ask-unbox-${brand}-${date.toISOString().slice(0, 10)}`;
}

export async function downloadChatTranscript(messages: Message[], scope: Brand) {
  const { default: JsPDF } = await import("jspdf");

  const pageWidth = 210;
  const pageHeight = 297;
  const margin = 18;
  const bottom = pageHeight - margin;
  const doc = new JsPDF({ unit: "mm", format: "a4" });
  const now = new Date();

  let y = margin;

  const write = (
    text: string,
    {
      size,
      style,
      color,
      indent = 0,
      font = "helvetica",
    }: {
      size: number;
      style: "normal" | "bold";
      color: number[];
      indent?: number;
      font?: "helvetica" | "courier";
    },
  ) => {
    doc.setFontSize(size);
    doc.setFont(font, style);
    doc.setTextColor(color[0], color[1], color[2]);
    const lineHeight = size * 0.5;
    for (const line of doc.splitTextToSize(text, pageWidth - margin * 2 - indent)) {
      if (y + lineHeight > bottom) {
        doc.addPage();
        y = margin;
      }
      doc.text(line, margin + indent, y);
      y += lineHeight;
    }
  };

  write("Ask Unbox", { size: 20, style: "bold", color: [42, 29, 74] });
  y += 1;
  write(`${scope.displayName || scope.name} · ${now.toLocaleString()}`, {
    size: 10,
    style: "normal",
    color: [120, 120, 130],
  });
  y += 6;

  for (const block of transcriptBlocks(messages)) {
    if (block.kind === "question") {
      y += 4;
      write(block.text, { size: 12, style: "bold", color: [30, 30, 40] });
      y += 2;
    } else if (block.kind === "answer") {
      write(block.text, { size: 10.5, style: "normal", color: [50, 50, 60] });
      y += 2;
    } else if (block.kind === "finding") {
      write(`• ${block.text}`, { size: 10.5, style: "normal", color: [50, 50, 60], indent: 4 });
    } else {
      write(block.text, { size: 8.5, style: "normal", color: [90, 90, 100], indent: 4, font: "courier" });
    }
  }

  doc.save(`${transcriptFileName(scope, now)}.pdf`);
}
